// components/memory/MemorySearchFilters.tsx

import React from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import SparkMainAppSearchFilters from 'components/common/SparkMainAppSearchFilters/V2';
import { usePopover } from 'hooks/common';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContextV2';

interface MemorySearchFiltersProps {
  memoryTypeDropdownOptions: Array<{ label: string; value: string }>;
}

const MemorySearchFilters: React.FC<MemorySearchFiltersProps> = ({
  memoryTypeDropdownOptions,
}) => {
  const { filter, handleItemTypeChange, handleQueryChange } =
    useSparkSearchContext();
  const { open, openPopover, closePopover, anchorEl } = usePopover();

  return (
    <SparkMainAppSearchFilters
      shouldHideAllFilters={true}
      placeholder='Search Knowledge Sources'
      containerStyles={{ marginTop: 0 }}
      textFieldContainerStyles={{ marginTop: 0 }}
      customEndAdornment={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!!filter.search?.length && (
            <IconButton onClick={() => handleQueryChange('')} size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '0 0 0 10px',
              borderLeft: filter.search?.length ? '1px solid #EEEEEE' : 'none',
              height: '30px',
              marginRight: '0',
            }}
          >
            <Box
              onClick={openPopover}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 6px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                maxWidth: '150px',
                backgroundColor: (theme) => theme.palette.primary.light,
                '&:hover': {
                  backgroundColor: (theme) =>
                    `${theme.palette.primary.light}CC`,
                },
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: (theme) => theme.palette.primary.main,
                  letterSpacing: '0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px',
                }}
              >
                {memoryTypeDropdownOptions.find(
                  (item) => item.value === filter.selectedItemType
                )?.label || memoryTypeDropdownOptions[0].label}
              </Typography>
              <ExpandMoreIcon
                fontSize='small'
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                }}
              />
            </Box>

            <Menu
              open={open}
              anchorEl={anchorEl}
              onClose={closePopover}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                mt: 1,
                '& .MuiPaper-root': {
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                },
              }}
            >
              {memoryTypeDropdownOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    handleItemTypeChange(option.value);
                    closePopover();
                  }}
                  selected={option.value === filter.selectedItemType}
                  sx={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    minWidth: '140px',
                    '&.Mui-selected': {
                      backgroundColor: '#f0f0f0',
                    },
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight:
                        option.value === filter.selectedItemType ? 500 : 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      }
    />
  );
};

export default MemorySearchFilters;
